import { useMemo } from 'react';
import { SelectInput } from '@interstate/components/SelectInput';
import { NumericInput } from '@interstate/components/NumericInput';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { useCustomerCardContext } from '../CustomerCardProvider';
import { NameFieldsWrapper } from '../CustomerCard.styles';
import { CustomerFieldsAccordionContainer } from './HousingAndEmploymentInfo.style';
import {
    HOUSING_STATUS_FIELD,
    MONTHLY_MORTGAGE_RENT_FIELD,
    YEARS_AT_ADDRESS_FIELD,
    MONTHS_AT_ADDRESS_FIELD,
    HOUSING_STATUS_OPTIONS
} from '../constants';
import { CustomerFieldsLayoutHeader } from './CustomerFieldsLayoutHeader';
import { DurationField } from './DurationField';
import type { ErrorState } from '@makemydeal/dr-dash-ui-utils';

export const CurrentAddress = () => {
    const { isLocked, formValues, handleInputChange, handleNumberInputChange, customerFormErrors } = useCustomerCardContext();

    const formErrors: {
        mortgageOrRentAmount: ErrorState;
        yearsAtCurrentAddress: ErrorState;
        monthsAtCurrentAddress: ErrorState;
    } = useMemo(
        () => ({
            mortgageOrRentAmount: (customerFormErrors?.housingInfo as Record<string, ErrorState>)?.mortgageOrRentAmount ?? {
                hasError: false,
                errorMessage: ''
            },
            yearsAtCurrentAddress: (customerFormErrors as Record<string, ErrorState>)?.yearsAtCurrentAddress ?? {
                hasError: false,
                errorMessage: ''
            },
            monthsAtCurrentAddress: (customerFormErrors as Record<string, ErrorState>)?.monthsAtCurrentAddress ?? {
                hasError: false,
                errorMessage: ''
            }
        }),
        [customerFormErrors]
    );

    const renderHousingStatusField = () => {
        return (
            <SelectInput
                name="housingStatus"
                displayLabel={true}
                label="Housing Status"
                value={formValues.housingInfo?.housingStatus ?? ''}
                onChange={(e) => handleInputChange(HOUSING_STATUS_FIELD, e.target.value || '')}
                options={HOUSING_STATUS_OPTIONS}
                disabled={isLocked}
                data-testid="customer-form-housing-status"
                size="small"
            />
        );
    };

    const renderMonthlyMortgageRentField = () => {
        return (
            <NumericInput
                name="monthlyMortgageRent"
                displayLabel={true}
                label="Monthly Mortgage/Rent"
                value={String(formValues.housingInfo?.mortgageOrRentAmount ?? '')}
                allowDecimal={true}
                autoInsertCommas={true}
                decimalMaxLength={2}
                decimalMinLength={0}
                inputPrefix="$"
                onChange={(e: InterstateOnChangeEvent<string>) =>
                    handleNumberInputChange(MONTHLY_MORTGAGE_RENT_FIELD, e.target.value)
                }
                disabled={isLocked}
                hasError={Boolean(formErrors.mortgageOrRentAmount.hasError)}
                errorMessage={formErrors.mortgageOrRentAmount.errorMessage || ''}
                data-testid="customer-form-monthly-mortgage-rent"
                size="small"
            />
        );
    };

    return (
        <CustomerFieldsAccordionContainer data-testid="current-address-section">
            <CustomerFieldsLayoutHeader>Current Address</CustomerFieldsLayoutHeader>
            <NameFieldsWrapper columns={4}>
                {renderHousingStatusField()}
                {renderMonthlyMortgageRentField()}
                <DurationField
                    label="Yrs. at Address"
                    value={String(formValues.yearsAtCurrentAddress ?? '')}
                    field={YEARS_AT_ADDRESS_FIELD}
                    error={formErrors.yearsAtCurrentAddress}
                />
                <DurationField
                    label="Mos. at Address"
                    value={String(formValues.monthsAtCurrentAddress ?? '')}
                    field={MONTHS_AT_ADDRESS_FIELD}
                    error={formErrors.monthsAtCurrentAddress}
                />
            </NameFieldsWrapper>
        </CustomerFieldsAccordionContainer>
    );
};
